import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Badge,
  Card,
  Combobox,
  Input,
  Label,
  Modal,
  MultiSelect,
  SimpleSelect,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import { DbJsonSchema, isTruthy } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { z } from 'zod';
import DescriptionList, { Description } from '~/components/DescriptionList';
import { useStoreUser } from '~/providers/StoreUserProvider';
import { OrderEditingFormSchema } from '~/types';
import {
  dayOfWeekOptions,
  endTimeOfDayOptions,
  overrideSummary,
  startTimeOfDayOptions,
  timezoneOptions,
  windowMinuteOptions,
} from './orderEditingOptions';

type Override = DbJsonSchema.StoreSettings.OrderEditingWindowOverride;

const newOverride = {
  label: null,
  ianaTimezone: null,
  daysOfWeek: [1, 2, 3, 4, 5],
  startMinuteOfDay: 540,
  endMinuteOfDay: 1020,
  windowMinutes: 10,
} satisfies Override;

function OverrideModal({
  values,
  onSubmit,
  onClose,
}: {
  /** absent when creating a new override */
  values?: Override;
  onSubmit: (override: Override) => void;
  onClose: () => void;
}) {
  const {
    store: { ianaTimezone },
  } = useStoreUser();

  const { control, handleSubmit, register } = useForm({
    resolver: zodResolver(
      DbJsonSchema.StoreSettings.orderEditingWindowOverride,
    ),
    // pinning the store's zone on new overrides, so the one an order is judged against is never implicit
    defaultValues: values ?? { ...newOverride, ianaTimezone },
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((override) => onSubmit(override))(event).catch(console.error);
  };

  return (
    <Modal
      show
      onClose={onClose}
      title={values ? 'Edit Override' : 'Add Override'}
      size="lg"
    >
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <Input
          id="order-editing-window-override-label"
          label="Name"
          details="Only shown here, to help you tell your overrides apart."
          {...register('label')}
        />

        <Controller
          control={control}
          name="daysOfWeek"
          render={({ field, fieldState }) => (
            <MultiSelect
              togglable
              label="Days"
              placeholder="Select days"
              details="The days an order must be placed on for this override to apply."
              options={dayOfWeekOptions}
              value={field.value
                .map((day) =>
                  dayOfWeekOptions.find((option) => option.value === `${day}`),
                )
                .filter(isTruthy)}
              onChange={(selected) =>
                field.onChange(selected.map(({ value }) => Number(value)))
              }
              error={
                fieldState.error?.message ?? fieldState.error?.root?.message
              }
            />
          )}
        />

        <Controller
          control={control}
          name="startMinuteOfDay"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="From"
              options={startTimeOfDayOptions}
              onChange={(value) => field.onChange(Number(value))}
              value={`${field.value}`}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="endMinuteOfDay"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="To"
              options={endTimeOfDayOptions}
              onChange={(value) => field.onChange(Number(value))}
              value={`${field.value}`}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="ianaTimezone"
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              {/* Combobox has no `details` prop, so the days and times above carry the explanation */}
              <Label>Timezone</Label>
              <Combobox
                label="Timezone"
                placeholder="Search timezones"
                options={timezoneOptions}
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value ?? null)}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="windowMinutes"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Edit Window"
              details="Overrides the default edit window for orders placed in this range."
              options={windowMinuteOptions}
              onChange={(value) => field.onChange(Number(value))}
              value={`${field.value}`}
              error={fieldState.error?.message}
            />
          )}
        />

        <Action variant="primary" type="submit">
          {values ? 'Save' : 'Create'}
        </Action>
      </form>
    </Modal>
  );
}

function OverrideListItem({
  override,
  storeTimezone,
  onEdit,
  onRemove,
}: {
  override: Override;
  storeTimezone?: string;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const {
    label,
    ianaTimezone,
    daysOfWeek,
    startMinuteOfDay,
    endMinuteOfDay,
    windowMinutes,
  } = override;
  const days = overrideSummary.days(daysOfWeek);
  const timeRange = overrideSummary.timeRange(startMinuteOfDay, endMinuteOfDay);
  const window = overrideSummary.window(windowMinutes);
  const timezone = ianaTimezone ?? storeTimezone;

  const descriptions: Description[] = [
    { term: 'Days', details: overrideSummary.dayNames(daysOfWeek) },
    { term: 'Placed Between', details: timeRange },
    { term: 'Timezone', details: timezone ?? 'Not set' },
    { term: 'Edit Window', details: window },
    {
      term: 'Applies To',
      details: timezone
        ? `Orders placed ${days} between ${timeRange} ${timezone} can be edited for ${window} after they're placed.`
        : 'This override is skipped until it has a timezone.',
    },
  ];

  return (
    <div className="rounded-md border bg-white p-3 shadow-xs">
      <Disclosure
        renderSummary={
          <SimpleSummary>
            <div className="flex w-full flex-row items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col items-start gap-1">
                <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
                  {label ?? days}
                </p>
                <p className="text-xs text-neutral-500 italic">
                  {[label && days, timeRange, timezone]
                    .filter(isTruthy)
                    .join(' · ')}
                </p>
              </div>

              <div className="flex flex-row items-center gap-2">
                <Badge>{window}</Badge>
                {/* buttons inside a summary toggle the disclosure without this */}
                <Action
                  icon="edit"
                  variant="ghost"
                  accessibilityLabel={`Edit ${label ?? days} override`}
                  onClick={(event) => {
                    event.preventDefault();
                    onEdit();
                  }}
                />
                <Action
                  icon="remove"
                  variant="ghost"
                  accessibilityLabel={`Remove ${label ?? days} override`}
                  onClick={(event) => {
                    event.preventDefault();
                    onRemove();
                  }}
                />
              </div>
            </div>
          </SimpleSummary>
        }
      >
        <div className="mt-1 px-2">
          <DescriptionList descriptions={descriptions} />
        </div>
      </Disclosure>
    </div>
  );
}

export default function OrderEditingWindowOverrides({
  onCommit,
}: {
  /** Saves the settings form; overrides persist on change rather than waiting for the floating save. */
  onCommit: () => void;
}) {
  const { control, getValues } =
    useFormContext<z.infer<typeof OrderEditingFormSchema>>();
  const {
    store: { ianaTimezone },
  } = useStoreUser();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'orderEditingWindowOverrides',
  });

  const [showForm, setShowForm] = useState(false);
  // undefined while creating a new override
  const [editIndex, setEditIndex] = useState<number>();

  const closeForm = () => setShowForm(false);

  const commitOverride = (override: Override) => {
    if (editIndex === undefined) append(override);
    else update(editIndex, override);
    closeForm();
    onCommit();
  };

  return (
    <div className="space-y-2">
      <Card.Line as="header">
        <Card.Heading as="h3">Overrides</Card.Heading>
        <Action
          icon="add"
          variant="ghost"
          accessibilityLabel="Add Override"
          onClick={() => {
            setEditIndex(undefined);
            setShowForm(true);
          }}
        />
      </Card.Line>

      <Card.Description>
        Use a different edit window for orders placed on certain days and times,
        each read in its own timezone. The first override an order matches wins;
        orders matching none use the edit window above.
      </Card.Description>

      <div className="pt-2">
        {!fields.length ? (
          <p className="text-sm text-neutral-400 italic">No overrides.</p>
        ) : (
          <ul className="space-y-2">
            {fields.map((field, index) => {
              const override = getValues(
                `orderEditingWindowOverrides.${index}`,
              );

              return (
                <li key={field.id}>
                  <OverrideListItem
                    override={override}
                    storeTimezone={ianaTimezone}
                    onEdit={() => {
                      setEditIndex(index);
                      setShowForm(true);
                    }}
                    onRemove={() => {
                      remove(index);
                      onCommit();
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* mounted only while open so a cancelled draft can't leak into the next open */}
      {showForm && (
        <OverrideModal
          values={
            editIndex === undefined
              ? undefined
              : getValues(`orderEditingWindowOverrides.${editIndex}`)
          }
          onSubmit={commitOverride}
          onClose={closeForm}
        />
      )}
    </div>
  );
}
